import { type JSX, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { Gruppierungskennung } from '../api/berichte';
import { Diagramm, type Diagrammpunkt, type Reihe, kleineBalkenmitte, kleineSkala, kleinerAnteil } from '../komponenten/Diagramm';

/**
 * DAS DIAGRAMM IM BERICHTS-WIDGET (Auftrag #40, Nachbesserung zu #38).
 *
 * Rückmeldung vom 01.10.2026: Die Balken nutzten nur die untere Hälfte, die
 * Monate standen als Liste daneben statt an den Balken, und der schraffierte
 * letzte Balken war nicht erklärt. Deshalb:
 *
 *   - Die Skala reicht von null bis zum größten Wert (`kleineSkala`) — es gibt
 *     keine Achse, an der man eine aufgerundete Grenze ablesen könnte.
 *   - Unter jeder Spalte steht ihr Name, kurz („Mai", „Jun"; das Jahr nur,
 *     wo es wechselt). Passen nicht alle, steht jede zweite oder n-te — nie
 *     überlappend. Die Zeile gehört zur Höhe des Bilds.
 *   - Über jedem Balken (Punkt) steht sein Wert, wenn er in die Spalte passt;
 *     sonst nur im Hinweis beim Überfahren.
 *
 * Wie viel passt, rechnet das Bild aus seiner gemessenen Breite und einer
 * geschätzten Textbreite (`textbreite`). Ohne Layout (jsdom) steht alles.
 *
 * Auftrag CRM-60: Stehen die Vergleiche als hellere Balken daneben, steht der
 * Wert über dem ERSTEN Balken der Spalte (`kleineBalkenmitte`), die Skala
 * reicht wie im Diagramm bis zum größten Wert ALLER Reihen, und der Hinweis
 * nennt die Vergleichswerte mit.
 */

const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Schriftgröße der Beschriftung und der Werte in px (stil.css, `.berichtsbild`). */
const SCHRIFT = 12;
/** Höhe der Beschriftungszeile und des Bands für die Werte in px. */
const ZEILE = 16;
/** Darunter ist die Fläche zu niedrig für einen Wert über dem Balken. */
const FLAECHE_MIT_WERTEN = 36;

/** Der Name einer Spalte: kurz an der Achse, das Jahr getrennt, lang im Hinweis. */
export type Spaltenname = { readonly kurz: string; readonly jahr: string | null; readonly lang: string };

/** Die Namen der Spalten aus Schlüssel und Bezeichnung der Ergebniszeilen. */
export function spaltennamen(
  zeilen: readonly { readonly schluessel: string | null; readonly bezeichnung: string }[],
  gruppierung: Gruppierungskennung | null,
): readonly Spaltenname[] {
  return zeilen.map((z) => {
    const s = z.schluessel ?? '';
    const monat = gruppierung === 'monat' ? /^(\d{4})-(\d{2})$/.exec(s) : null;
    if (monat !== null) {
      const m = Number(monat[2]) - 1;
      return { kurz: MONATE[m] ?? monat[2]!, jahr: monat[1]!, lang: `${MONATSNAMEN[m] ?? monat[2]!} ${monat[1]!}` };
    }
    const quartal = gruppierung === 'quartal' ? /^(\d{4})-Q(\d)$/.exec(s) : null;
    if (quartal !== null) return { kurz: `Q${quartal[2]!}`, jahr: quartal[1]!, lang: z.bezeichnung };
    return { kurz: kuerzen(z.bezeichnung, 12), jahr: null, lang: z.bezeichnung };
  });
}

function kuerzen(text: string, laenge: number): string {
  return text.length > laenge ? `${text.slice(0, laenge - 1)}…` : text;
}

/**
 * Die Beschriftung unter den Spalten, wenn jede `jede`-te steht ('' für die
 * übrigen). Das Jahr steht an der ersten gezeigten Spalte eines neuen Jahres —
 * gemessen an der zuletzt GEZEIGTEN, nicht an der Nachbarspalte.
 */
export function achsenbeschriftung(namen: readonly Spaltenname[], jede: number): readonly string[] {
  let jahrVorher: string | null = null;
  return namen.map((n, i) => {
    if (i % jede !== 0) return '';
    const wechsel = i > 0 && n.jahr !== null && jahrVorher !== null && n.jahr !== jahrVorher;
    jahrVorher = n.jahr;
    return wechsel ? `${n.kurz} '${n.jahr!.slice(2)}` : n.kurz;
  });
}

/** Geschätzte Breite eines Texts in px — die Ziffern und Buchstaben der Hausschrift sind im Mittel 0,58 em breit. */
export function textbreite(text: string, schrift = SCHRIFT): number {
  return text.length * schrift * 0.58;
}

/**
 * Jede wievielte Spalte beschriftet wird: die kleinste Zahl, bei der jede
 * gezeigte Beschriftung mit etwas Luft in ihre Spalten passt. Spaltenbreite 0
 * (nicht gemessen): jede.
 */
export function jedeWievielte(namen: readonly Spaltenname[], spaltenbreite: number): number {
  if (spaltenbreite <= 0) return 1;
  for (let k = 1; k < namen.length; k += 1) {
    if (achsenbeschriftung(namen, k).every((t) => textbreite(t) + 6 <= k * spaltenbreite)) return k;
  }
  return Math.max(1, namen.length);
}

/**
 * Welche Werte über den Balken stehen: die vollen Texte, wenn alle in ihre
 * Spalte passen, sonst die kurzen („12 T."), sonst keine. Ungemessen: die vollen.
 */
export function werteUeberBalken(
  voll: readonly string[],
  kurz: readonly string[],
  spaltenbreite: number,
  hoehe: number,
): readonly string[] | null {
  if (spaltenbreite <= 0) return voll;
  if (hoehe - 2 * ZEILE < FLAECHE_MIT_WERTEN) return null;
  const passt = (texte: readonly string[]): boolean => texte.every((t) => textbreite(t) + 4 <= spaltenbreite);
  return passt(voll) ? voll : passt(kurz) ? kurz : null;
}

/**
 * Der Hinweis einer Spalte beim Überfahren: „Oktober 2026, läuft noch: 12,0
 * Tage", mit Vergleichen „…; Angebotsdauer · Vorjahr: 10,0 Tage" (CRM-60).
 */
export function spaltenhinweis(name: Spaltenname, p: Diagrammpunkt, vergleiche: readonly string[] = []): string {
  return [`${name.lang}${p.unvollstaendig ? ', läuft noch' : ''}: ${p.wertText}`, ...vergleiche].join('; ');
}

/** Die Werte, die die Skala der kleinen Fassung bestimmen — dieselben wie im Diagramm. */
function skalenwerte(punkte: readonly Diagrammpunkt[], reihen: readonly Reihe[] | undefined, gestapelt: boolean): number[] {
  const von = (werte: readonly (number | null)[]): number[] => werte.flatMap((w) => (w === null ? [] : [w]));
  if (reihen === undefined) return von(punkte.map((p) => p.wert));
  const links = reihen.filter((r) => r.achse === 'links');
  if (!gestapelt) return links.flatMap((r) => von(r.werte));
  /* Gestapelt sind die Punkte die Summen der Spalten — die Höhe des Stapels. */
  return [...von(punkte.map((p) => p.wert)), ...links.filter((r) => r.art !== 'balken').flatMap((r) => von(r.werte))];
}

/** Die gemessene Größe eines Elements — 0 × 0 ohne Layout. */
function useGroesse(): { ref: RefObject<HTMLDivElement | null>; breite: number; hoehe: number } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [g, setzeG] = useState({ breite: 0, hoehe: 0 });
  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r !== undefined) setzeG({ breite: Math.round(r.width), hoehe: Math.round(r.height) });
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const beobachter = new ResizeObserver(([e]) => {
      if (e === undefined) return;
      const neu = { breite: Math.round(e.contentRect.width), hoehe: Math.round(e.contentRect.height) };
      setzeG((alt) => (alt.breite === neu.breite && alt.hoehe === neu.hoehe ? alt : neu));
    });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);
  return { ref, ...g };
}

export function Berichtsbild({
  art,
  punkte,
  namen,
  mitWerten,
  achse,
  beschriftung,
  reihen,
  gestapelt = false,
}: {
  readonly art: 'balken' | 'linie';
  readonly punkte: readonly Diagrammpunkt[];
  /**
   * Auftrag CRM-46: die Reihen eines aufgeteilten Berichts (je Wert eine);
   * `punkte` bleiben die Summen der Spalten — fuer Hinweise und die Werte
   * ueber gestapelten Balken.
   */
  readonly reihen?: readonly Reihe[] | undefined;
  readonly gestapelt?: boolean;
  readonly namen: readonly Spaltenname[];
  /** Werte über den Balken — nur ohne Legende daneben, die sie schon nennt. */
  readonly mitWerten: boolean;
  readonly achse: (wert: number) => string;
  readonly beschriftung: string;
}): JSX.Element {
  const { ref, breite, hoehe } = useGroesse();
  const n = Math.max(punkte.length, 1);
  const spalte = breite / n;
  const unten = achsenbeschriftung(namen, jedeWievielte(namen, spalte));
  /* Balken nebeneinander: Der Wert steht über dem ersten, nicht über der Mitte der Spalte. */
  const nebeneinander =
    reihen !== undefined && !gestapelt && reihen[0]?.art === 'balken' ? reihen.filter((r) => r.art === 'balken').length : 1;
  const lage = (i: number): number => (nebeneinander > 1 ? kleineBalkenmitte(i, n, nebeneinander) : (i + 0.5) / n);
  /* Der Text steht mittig über dem Balken — so weit links der Mitte ist die Spalte für ihn schmaler. */
  const versatz = Math.abs(lage(0) - 0.5 / n) * breite;
  const werte = mitWerten
    ? werteUeberBalken(
        punkte.map((p) => (p.wert === null ? '' : p.wertText)),
        punkte.map((p) => (p.wert === null ? '' : achse(p.wert))),
        breite > 0 ? Math.max(1, spalte - 2 * versatz) : 0,
        hoehe,
      )
    : null;
  const skala = kleineSkala(skalenwerte(punkte, reihen, gestapelt));
  const vergleichsreihen = (reihen ?? []).filter((r) => r.stufe > 0);
  const vergleichstexte = (i: number): string[] =>
    vergleichsreihen.flatMap((r) => (r.werte[i] == null ? [] : [`${r.name}: ${r.texte[i] ?? ''}`]));

  return (
    <div className="berichtsbild" ref={ref}>
      <div className={werte === null ? 'berichtsbild__flaeche' : 'berichtsbild__flaeche berichtsbild__flaeche--mit-werten'}>
        <div className="berichtsbild__innen">
          <Diagramm
            klein
            {...(reihen === undefined
              ? { art, punkte }
              : {
                  kategorien: punkte.map((p) => ({ schluessel: p.schluessel, beschriftung: p.beschriftung })),
                  reihen,
                  gestapelt,
                })}
            beschriftung={beschriftung}
            achse={achse}
            hinweise={punkte.map((p, i) => (namen[i] === undefined ? p.wertText : spaltenhinweis(namen[i], p, vergleichstexte(i))))}
          />
          {werte?.map((t, i) => {
            const w = punkte[i]?.wert ?? null;
            if (w === null || t === '') return null;
            return (
              <span
                key={punkte[i]!.schluessel}
                className="berichtsbild__wert"
                data-wert=""
                aria-hidden="true"
                style={{ left: `${String(lage(i) * 100)}%`, bottom: `${String(kleinerAnteil(skala, Math.max(w, 0)) * 100)}%` }}
              >
                {t}
              </span>
            );
          })}
        </div>
      </div>
      <div className="berichtsbild__achse" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${String(n)}, minmax(0, 1fr))` }}>
        {unten.map((t, i) => (
          <span key={punkte[i]?.schluessel ?? String(i)} className="berichtsbild__name" data-achse={t === '' ? undefined : ''}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
